"use client";

import { Fragment, memo, useMemo, useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { ChatTurn, ToolCallEntry } from "@/lib/planner/chatTurns";
import {
  activityView,
  hotelPreviews,
  hotelDetailsPath,
  currentRateNotices,
  RETRY_RATES_MESSAGE,
} from "@/lib/planner/activityTimeline";
import { mcpAppPayload } from "@/lib/mcpApps/payload";
import HotelPreviewHeader from "./HotelPreviewHeader";
import McpAppFrame from "./McpAppFrame";
import MarkdownContent from "./MarkdownContent";
import ToolCallIndicator from "./ToolCallIndicator";

const HotelCards = memo(function HotelCards({
  call,
  calls,
}: {
  call: ToolCallEntry;
  calls: ToolCallEntry[];
}) {
  const hotels = useMemo(() => hotelPreviews(call), [call]);
  return (
    <>
      {hotels.map((hotel, index) => (
        <div
          key={index}
          className="rounded-lg border border-border bg-card p-3"
        >
          <HotelPreviewHeader
            hotel={hotel}
            href={hotelDetailsPath(hotel, call, calls)}
          />
          {hotel.facts.slice(0, 3).map((fact, i) => (
            <p key={i} className="mt-1 text-sm text-muted-foreground">
              {fact}
            </p>
          ))}
          {hotel.facts.length > 3 ? (
            <Accordion type="single" collapsible>
              <AccordionItem value="details" variant="noBorder">
                <AccordionTrigger className="py-2 text-sm text-foreground">
                  More about {hotel.name}
                </AccordionTrigger>
                <AccordionContent>
                  {hotel.facts.slice(3).map((fact, i) => (
                    <p key={i} className="mb-1 text-sm text-muted-foreground">
                      {fact}
                    </p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : null}
        </div>
      ))}
    </>
  );
});

export default function ActivityTimeline({
  turn,
  onRetryRates,
  retryDisabled,
  toolLabel,
  onAppMessage,
  appBusy,
}: {
  turn: ChatTurn;
  onRetryRates?: (message: string) => void;
  retryDisabled?: boolean;
  toolLabel: (call: ToolCallEntry) => string;
  /** Sends a chat message on behalf of an MCP App view (GON-7817). */
  onAppMessage?: (message: string) => void;
  appBusy?: boolean;
}) {
  const { items } = activityView(turn);
  const calls = turn.toolCalls ?? [];
  const notices = currentRateNotices(calls);
  const [open, setOpen] = useState(true);
  const appsEnabled = useFeatureFlagEnabled("concierge-mcp-apps") === true;
  if (!items.length) return null;
  const callContent = (call: ToolCallEntry) => {
    const cards = <HotelCards call={call} calls={calls} />;
    const payload = appsEnabled ? mcpAppPayload(call) : null;
    if (!payload) return cards;
    return (
      <McpAppFrame
        payload={payload}
        toolArguments={call.args}
        busy={Boolean(appBusy || turn.isStreaming)}
        onSendMessage={onAppMessage}
        fallback={cards}
      />
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <Accordion
        type="single"
        collapsible
        value={open ? "activity" : ""}
        onValueChange={(value) => setOpen(value === "activity")}
      >
        <AccordionItem value="activity" variant="noBorder">
          <AccordionTrigger className="py-1 text-sm text-muted-foreground">
            {open ? "Hide activity" : "Show activity"} ({calls.length}{" "}
            {calls.length === 1 ? "check" : "checks"})
          </AccordionTrigger>
          <AccordionContent>
            <div className="mt-3 flex flex-col gap-3 border-l border-border pl-3">
              {items.map((item, index) => {
                if (item.type === "text")
                  return <MarkdownContent key={index} content={item.content} />;
                const call = calls.find(
                  (candidate) => candidate.id === item.toolCallId
                );
                if (!call) return null;
                const label = toolLabel(call);
                return (
                  <div key={item.toolCallId} className="flex flex-col gap-2">
                    <ToolCallIndicator
                      toolName={call.name}
                      label={label}
                      isLoading={Boolean(
                        turn.isStreaming && call.status === "calling"
                      )}
                    />
                    {callContent(call)}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {notices.length > 0 && !open
        ? calls.map((call) => (
            <Fragment key={call.id}>{callContent(call)}</Fragment>
          ))
        : null}
      {notices.map(({ call, notice }) => (
        <p key={call.id} className="text-sm text-muted-foreground">
          {notice?.message}
        </p>
      ))}
      {onRetryRates &&
      notices.some((item) => item.notice?.status !== "unavailable") &&
      turn.errorReason !== "turns" ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          disabled={retryDisabled || turn.isStreaming}
          onClick={() => onRetryRates(RETRY_RATES_MESSAGE)}
        >
          Retry rates
        </Button>
      ) : null}
    </div>
  );
}
